import { apiClient, streamText } from './apiClient';
import type { DailyTasksResponse, GreetingDto, TaskItemDto } from '../types/task';
import { relanceMinimumAmountService } from './relanceMinimumAmountService';

export type RelanceStatusFilter = 'Active' | 'Deactivated' | 'Paid';
export type SnoozeDuration = 'Hours24' | 'Days7' | 'Days14' | 'Custom' | 'Indefinite';

export interface DeactivateTaskOptions {
  snoozeDuration: SnoozeDuration;
  customDate?: string | null;
  note?: string | null;
}

export const tasksService = {
  getDailyTasks: async (status: RelanceStatusFilter = 'Active', page?: number, pageSize?: number) => {
    // A minimum-amount save rewrites the active list and its greeting; read them once it is done.
    if (status === 'Active') await relanceMinimumAmountService.settled();
    const params = new URLSearchParams({ status });
    if (page !== undefined) params.set('page', String(page));
    if (pageSize !== undefined) params.set('pageSize', String(pageSize));
    return apiClient.get<DailyTasksResponse>(`/tasks/daily?${params.toString()}`);
  },

  getGreeting: (): Promise<string | null> =>
    apiClient.get<GreetingDto>('/tasks/greeting').then(data => data?.message ?? null),

  streamGreeting: async function* (lang: string, noAi = false, signal?: AbortSignal): AsyncGenerator<string> {
    await relanceMinimumAmountService.settled();
    yield* streamText(`/tasks/greeting/stream?language=${lang}${noAi ? '&noAi=true' : ''}`, signal);
  },

  getTaskById: (taskId: string) => apiClient.get<TaskItemDto>(`/tasks/${taskId}`),

  deactivateTask: (taskId: string, options: DeactivateTaskOptions): Promise<void> =>
    apiClient.post(`/tasks/${taskId}/deactivate`, options),

  reactivateTask: (taskId: string, reason?: string | null): Promise<void> =>
    apiClient.post(`/tasks/${taskId}/reactivate`, { reason: reason ?? null }),

  recordPayment: (taskId: string, amountReceived: number): Promise<void> =>
    apiClient.post(`/tasks/${taskId}/payment`, { amountReceived }),
};
